import React, { useState, useRef, useEffect } from "react"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import parse from "html-react-parser"
import TTMiddleBg from "./TTMiddleBg"

const QuoteText = ({ text, quoteUp, quoteDown }) => {
  return (
    <div className="relative flex flex-col lg:flex-row w-full max-w-[894px] justify-center items-center py-8">
      {/* Left Quote */}
      <span className="absolute top-0 left-2 lg:left-0 text-5xl text-primary transform -translate-y-2 -translate-x-2">
        {quoteUp}
      </span>
      {/* Text Content */}
      <Typography
        component="h3"
        variant="h2"
        className="text-text text-left leading-relaxed pt-8 lg:pl-8"
      >
        {text}
      </Typography>
      {/* Right Quote */}
      <span className="absolute bottom-0 right-0 text-5xl text-primary transform translate-y-2">
        {quoteDown}
      </span>
    </div>
  )
}

const renderQuoteIcon = (iconURL, direction, wSize, hSize) => {
  return (
    <div
      className={`w-[${wSize}px] h-[${hSize}px]
      ${direction === "up" ? "rotate-0" : "rotate-180"}
    `}
    >
      <img
        src={iconURL}
        alt="Quote Icon"
        style={{ width: `${wSize}px`, height: `${hSize}px` }}
      />
    </div>
  )
}

// Audio icons from HeroIcons
const VolumeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-6 h-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
    />
  </svg>
)

const VolumeOnIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="w-6 h-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z"
    />
  </svg>
)

const TTVideoBanner = () => {
  const [isMuted, setIsMuted] = useState(true)
  const videoRef = useRef(null)

  const toggleMute = (isAudioOn) => {
    setIsMuted(!isAudioOn)

    if (videoRef.current) {
      videoRef.current.muted = !isAudioOn
    }
  }

  return (
    <div className="relative w-full">
      <div
        className="w-full max-w-w2_xl px-4 md:px-px1_l xl:px-px1_xl md:mb-[64px] mx-auto"
        style={{ zIndex: 10 }}
      >
        <CMSRender
          contentType="T&T Video Banner"
          renderItem={(data) => {
            return (
              <div className="flex flex-col-reverse lg:flex-row items-center gap-10 md:gap-[70px]">
                {/* Left side: Video Container */}
                <div className="w-full max-w-[405px] lg:max-w-[505px] lg:w-1/3 shrink-0">
                  <div className="relative w-full aspect-[505/584] md:min-w-[312px] rounded-[50px] overflow-hidden">
                    <video
                      playsInline
                      autoPlay
                      muted
                      loop
                      ref={videoRef}
                      className="w-full h-full object-cover"
                    >
                      <source src={data.video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>

                    {/* Volume Control */}
                    <div className="absolute bottom-4 right-4 bg-paper bg-opacity-70 rounded-full p-2 cursor-pointer">
                      <Checkbox
                        isOn={!isMuted}
                        onChange={toggleMute}
                        render={({ isOn, toggle }) => (
                          <div
                            onClick={toggle}
                            className="flex items-center justify-center"
                          >
                            {isOn ? <VolumeOnIcon /> : <VolumeOffIcon />}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Right side: Quote */}
                <div className="w-full lg:w-2/3 md:pl-10">
                  <QuoteText
                    text={
                      data.title && typeof data.title === "string"
                        ? parse(data.title)
                        : data.title
                    }
                    quoteUp={renderQuoteIcon(data.quote_icon, "up", 50, 40)}
                    quoteDown={renderQuoteIcon(data.quote_icon, "down", 24, 20)}
                  />
                </div>
              </div>
            )
          }}
        />
      </div>

      <TTMiddleBg />
    </div>
  )
}

export default TTVideoBanner
